import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileDown, RotateCcw, Share2 } from 'lucide-react';
import Button from '../common/Button';
import ShareModal from './ShareModal';
import { TestResults } from '../../types';

interface ResultsHeaderProps {
  results: TestResults;
  onRestart: () => void;
  onDownloadPDF: () => void;
}

const ResultsHeader: React.FC<ResultsHeaderProps> = ({
  results,
  onRestart,
  onDownloadPDF
}) => {
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  return (
    <>
      <motion.div
        className="bg-white rounded-xl shadow-card p-8 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
          <div className="flex-1">
            <motion.h1 
              className="text-3xl lg:text-4xl font-bold text-gray-800 mb-2"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              Your Export Readiness Results
            </motion.h1>
            
            <motion.div
              className="flex flex-col sm:flex-row sm:items-center gap-2 text-gray-600"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              <span>Completed on {new Date(results.completedAt).toLocaleDateString()}</span>
              <span className="hidden sm:inline">•</span>
              <span className="text-sm">Assessment took approximately 60 minutes</span>
            </motion.div>
          </div>
          
          <motion.div 
            className="flex flex-wrap gap-3"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Button
              variant="outline"
              leftIcon={<RotateCcw />}
              onClick={onRestart}
              className="hover:bg-gray-50 transition-colors"
            >
              Restart Test
            </Button>
            
            <Button
              variant="outline"
              leftIcon={<Share2 />}
              onClick={() => setIsShareModalOpen(true)}
              className="hover:bg-gray-50 transition-colors"
            >
              Share Results
            </Button>
            
            <Button
              variant="primary"
              leftIcon={<FileDown />}
              onClick={onDownloadPDF}
              className="shadow-lg hover:shadow-xl transition-shadow"
            >
              Download PDF
            </Button>
          </motion.div>
        </div>
      </motion.div>

      <ShareModal 
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        results={results}
      />
    </>
  );
};

export default ResultsHeader;